import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { en } from "./translations.en";
import { lv } from "./translations.lv";

const TRANSLATIONS = { en, lv };
const STORAGE_KEY = "tc_lang";

const I18nContext = createContext(null);

function resolve(obj, path) {
  if (path == null) return undefined;
  const keys = path.split(".");
  let value = obj;
  for (const k of keys) {
    if (value == null) return undefined;
    value = value[k];
  }
  return value;
}

export function I18nProvider({ children, defaultLang = "en" }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return defaultLang;
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "lv") return stored;
    const browser = (window.navigator?.language || "").toLowerCase();
    if (browser.startsWith("lv")) return "lv";
    return defaultLang;
  });

  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === "en" || next === "lv") setLangState(next);
  }, []);

  const t = useCallback(
    (path, fallback) => {
      const value = resolve(TRANSLATIONS[lang], path);
      if (value !== undefined) return value;
      const enValue = resolve(TRANSLATIONS.en, path);
      if (enValue !== undefined) return enValue;
      return fallback !== undefined ? fallback : path;
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
